import React, { useState, useEffect } from 'react';
import { ENV } from '../../../config/env';
import { AdminUserContext } from './context';

/**
 * AdminUserProvider - Secure server-side authentication for admin features
 * 
 * This component provides admin authentication using secure server-side validation
 * via Cloudflare Pages Functions. It replaces the previous client-side password
 * approach which was vulnerable to security issues.
 * 
 * Security features:
 * - Server-side password validation (never exposed to client)
 * - HttpOnly session cookies with TTL
 * - Automatic session expiration checking
 * - CSRF protection via SameSite cookies
 * - Feature flag integration for production safety
 */
export const AdminUserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for existing authentication on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);

      // Complete auth bypass in development mode
      const isAuthDisabledInDev = import.meta.env.DEV &&
        import.meta.env.VITE_DISABLE_AUTH_IN_DEV === 'true';

      if (isAuthDisabledInDev) {
        console.log('🔓 Dev mode: Auth completely disabled - auto-authenticating');
        setIsAuthenticated(true);
        setUser({
          email: 'dev@avenue.so',
          role: 'admin',
          firstName: 'Dev',
          lastName: 'User'
        });
        setError(null);
        setLoading(false);
        return;
      }

      // Skip server endpoint in development if dev auth is explicitly enabled
      const isDevAuthEnabled = import.meta.env.DEV &&
        import.meta.env.VITE_ENABLE_ADMIN_DEV_AUTH === 'true';

      if (isDevAuthEnabled) {
        console.log('Dev mode: Skipping server auth check, using localStorage fallback');
        const devSession = localStorage.getItem('admin_dev_session');
        if (devSession) {
          try {
            const session = JSON.parse(devSession);
            if (session.isAuthenticated && Date.now() < session.expiresAt) {
              setIsAuthenticated(true);
              setUser({
                email: 'admin@avenue.so',
                role: 'admin',
                firstName: 'Admin',
                lastName: 'User'
              });
              setError(null);
              setLoading(false);
              return;
            } else {
              localStorage.removeItem('admin_dev_session');
            }
          } catch (parseError) {
            localStorage.removeItem('admin_dev_session');
          }
        }

        setIsAuthenticated(false);
        setUser(null);
        setLoading(false);
        return;
      }
      
      // Check session via secure server endpoint
      const response = await fetch('/api/admin/auth', {
        method: 'GET',
        credentials: 'include', // Include HttpOnly cookies
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.isAuthenticated && data.user) {
          setIsAuthenticated(true);
          setUser(data.user);
          setError(null);
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (err) {
      console.warn('Admin auth check failed:', err);
      
      // Development fallback: if server endpoint fails, use localStorage (only if explicitly enabled)
      const isDevAuthEnabled = import.meta.env.DEV && 
        import.meta.env.VITE_ENABLE_ADMIN_DEV_AUTH === 'true';
      
      if (isDevAuthEnabled) {
        console.log('Dev mode: Using localStorage fallback for admin auth check');
        const devSession = localStorage.getItem('admin_dev_session');
        if (devSession) {
          try {
            const session = JSON.parse(devSession);
            if (session.isAuthenticated && Date.now() < session.expiresAt) {
              setIsAuthenticated(true);
              setUser({
                email: 'admin@avenue.so',
                role: 'admin',
                firstName: 'Admin',
                lastName: 'User'
              });
              setError(null);
              setLoading(false);
              return;
            } else {
              localStorage.removeItem('admin_dev_session');
            }
          } catch (parseError) {
            localStorage.removeItem('admin_dev_session');
          }
        }
      }
      
      setIsAuthenticated(false);
      setUser(null);
      setError('Authentication check failed');
    } finally {
      setLoading(false);
    }
  };

  const login = async (password) => {
    try {
      setError(null);
      
      // Skip server endpoint in development if dev login is explicitly enabled
      const isDevLoginEnabled = import.meta.env.DEV && 
        import.meta.env.VITE_ENABLE_ADMIN_DEV_LOGIN === 'true';
      
      if (isDevLoginEnabled) {
        console.log('Dev mode: Skipping server auth, using localStorage fallback');
        
        // Require explicit password configuration (no hardcoded default)
        const devAdminPassword = import.meta.env.VITE_ADMIN_PASSWORD_DEV;
        
        if (!devAdminPassword) {
          console.error('VITE_ADMIN_PASSWORD_DEV must be set when VITE_ENABLE_ADMIN_DEV_LOGIN is enabled');
          setError('Development authentication misconfigured');
          setIsAuthenticated(false);
          setUser(null);
          return false;
        }
        
        if (password === devAdminPassword) {
          // Create development session
          const devSession = {
            isAuthenticated: true,
            issuedAt: Date.now(),
            expiresAt: Date.now() + (12 * 60 * 60 * 1000), // 12 hours
            role: 'admin'
          };
          
          localStorage.setItem('admin_dev_session', JSON.stringify(devSession));
          
          setIsAuthenticated(true);
          setUser({
            email: 'admin@avenue.so',
            role: 'admin',
            firstName: 'Admin',
            lastName: 'User'
          });
          setError(null);
          return true;
        } else {
          setError('Invalid credentials');
          setIsAuthenticated(false);
          setUser(null);
          return false;
        }
      }
      
      // Try secure server endpoint first
      try {
        const response = await fetch('/api/admin/auth', {
          method: 'POST',
          credentials: 'include', // Include HttpOnly cookies
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ password })
        });
        
        // Safely parse JSON response
        let data;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            data = await response.json();
          } else {
            // Non-JSON response (likely HTML error page)
            data = { 
              error: `Server error: ${response.status} ${response.statusText || 'Unknown error'}` 
            };
          }
        } catch (parseError) {
          // Malformed or empty JSON
          data = { 
            error: `Response parsing failed: ${response.status} ${response.statusText || 'Invalid response format'}` 
          };
        }
        
        if (response.ok && data.success) {
          setIsAuthenticated(true);
          setUser(data.user);
          setError(null);
          return true;
        } else {
          // Server auth failed - throw error to trigger dev fallback
          throw new Error(data.error || `Server auth failed: ${response.status}`);
        }
      } catch (fetchError) {
        // Development fallback: if server endpoint fails, use simple password check (only if explicitly enabled)
        const isDevLoginEnabled = import.meta.env.DEV && 
          import.meta.env.VITE_ENABLE_ADMIN_DEV_LOGIN === 'true';
        
        if (isDevLoginEnabled) {
          console.log('Dev mode: Using localStorage fallback for admin login');
          
          // Require explicit password configuration (no hardcoded default)
          const devAdminPassword = import.meta.env.VITE_ADMIN_PASSWORD_DEV;
          
          if (!devAdminPassword) {
            console.error('VITE_ADMIN_PASSWORD_DEV must be set when VITE_ENABLE_ADMIN_DEV_LOGIN is enabled');
            setError('Development authentication misconfigured');
            setIsAuthenticated(false);
            setUser(null);
            return false;
          }
          
          if (password === devAdminPassword) {
            // Create development session
            const devSession = {
              isAuthenticated: true,
              issuedAt: Date.now(),
              expiresAt: Date.now() + (12 * 60 * 60 * 1000), // 12 hours
              role: 'admin'
            };
            
            localStorage.setItem('admin_dev_session', JSON.stringify(devSession));
            
            setIsAuthenticated(true);
            setUser({
              email: 'admin@avenue.so',
              role: 'admin',
              firstName: 'Admin',
              lastName: 'User'
            });
            setError(null);
            return true;
          } else {
            setError('Invalid credentials');
            setIsAuthenticated(false);
            setUser(null);
            return false;
          }
        } else {
          // Production mode - show generic error
          console.warn('Admin login failed:', fetchError);
          setError('Login request failed');
          setIsAuthenticated(false);
          setUser(null);
          return false;
        }
      }
    } catch (err) {
      // This should not be reached in normal operation
      console.error('Unexpected login error:', err);
      setError('Unexpected error during login');
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  };

  const logout = async () => {
    try {
      // Clear session via server endpoint
      await fetch('/api/admin/auth', {
        method: 'DELETE',
        credentials: 'include'
      });
    } catch (err) {
      console.warn('Logout request failed:', err);
    }
    
    // Development fallback: clear localStorage session (if dev auth was enabled)
    const isDevAuthEnabled = import.meta.env.DEV && (
      import.meta.env.VITE_ENABLE_ADMIN_DEV_AUTH === 'true' ||
      import.meta.env.VITE_ENABLE_ADMIN_DEV_LOGIN === 'true'
    );
    
    if (isDevAuthEnabled) {
      localStorage.removeItem('admin_dev_session');
    }
    
    // Always clear client state regardless of server response
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
  };

  const hasAdminAccess = () => {
    // Check authentication, role, and feature flag
    const isAdminFeatureEnabled = ENV.ENABLE_ADMIN_FEATURES === 'true' || 
                                 ENV.ENABLE_ADMIN_FEATURES === true ||
                                 import.meta.env.DEV; // Always enabled in development
    
    return isAuthenticated && 
           user?.role === 'admin' && 
           isAdminFeatureEnabled;
  };

  const contextValue = {
    user,
    loading,
    error,
    isAuthenticated,
    login,
    logout,
    checkAuthStatus,
    hasAdminAccess
  };

  return (
    <AdminUserContext.Provider value={contextValue}>
      {children}
    </AdminUserContext.Provider>
  );
};

export default AdminUserProvider;